import { mergeAttributes, Node } from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";
import { articleDocumentToEditor, editorToArticleDocument, mediaPreviewUrl, type EditorNode } from "./editor-document";
import { canUpload, duplicateMediaFromError, findDuplicate, hasSameNameAndMime, mergeMediaAssets, type MediaLibraryStatus } from "./media-library-state";
import type { ArticleDocument, MediaAsset } from "./types";

type PickerMode = "image" | "video" | null;
type Props = { value: ArticleDocument; media: MediaAsset[]; articleId?: string; onChange: (value: ArticleDocument) => void; onMediaChange: (assets: MediaAsset[]) => void };

const ArticleImage = Node.create({
  name: "articleImage", group: "block", atom: true, draggable: true, selectable: true,
  addAttributes() { return { mediaId: { default: "" }, src: { default: "" }, alt: { default: "" }, caption: { default: null } }; },
  parseHTML() { return [{ tag: "figure[data-article-image]" }]; },
  renderHTML({ HTMLAttributes }) {
    const { mediaId, src, alt, caption } = HTMLAttributes;
    return ["figure", mergeAttributes({ "data-article-image": "", "data-media-id": mediaId }), ["img", { src, alt, draggable: "false" }], ...(caption ? [["figcaption", {}, String(caption)]] : [])] as never;
  },
});

const ArticleVideo = Node.create({
  name: "articleVideo", group: "block", atom: true, draggable: true, selectable: true,
  addAttributes() {
    return {
      mediaId: { default: "" }, src: { default: "" }, posterMediaId: { default: null }, poster: { default: "" },
      caption: { default: null }, autoplay: { default: false }, loop: { default: false }, muted: { default: true }, controls: { default: true },
    };
  },
  parseHTML() { return [{ tag: "figure[data-article-video]" }]; },
  renderHTML({ HTMLAttributes }) {
    const { mediaId, src, poster, caption, autoplay, loop, muted, controls } = HTMLAttributes;
    return ["figure", mergeAttributes({ "data-article-video": "", "data-media-id": mediaId }), ["video", { src, poster, controls, autoplay, loop, muted, playsinline: "true" }], ...(caption ? [["figcaption", {}, String(caption)]] : [])] as never;
  },
});

const ArticleUnsupported = Node.create({
  name: "articleUnsupported", group: "block", atom: true, selectable: true,
  addAttributes() { return { raw: { default: {} } }; },
  parseHTML() { return [{ tag: "aside[data-article-unsupported]" }]; },
  renderHTML({ HTMLAttributes }) {
    const type = typeof HTMLAttributes.raw?.type === "string" ? HTMLAttributes.raw.type : "unknown";
    return ["aside", mergeAttributes({ "data-article-unsupported": "", "data-block-type": type }), "Неподдерживаемый блок: " + type];
  },
});

const ArticleNote = Node.create({
  name: "articleNote", group: "block", content: "inline*",
  addAttributes() { return { title: { default: null } }; },
  parseHTML() { return [{ tag: "aside[data-article-note]" }]; },
  renderHTML({ HTMLAttributes }) { return ["aside", mergeAttributes({ "data-article-note": "" }, HTMLAttributes), 0]; },
});
const CitedBlockquote = Blockquote.extend({
  addAttributes() { return { cite: { default: null } }; },
  renderHTML({ HTMLAttributes }) { return ["blockquote", mergeAttributes(HTMLAttributes), 0]; },
});

function safeLink(value: string) {
  const href = value.trim();
  if (href.startsWith("/") || href.startsWith("#") || href.startsWith("mailto:")) return href;
  try { return new URL(href).protocol === "https:" ? href : null; } catch { return null; }
}
function mediaPreview(asset: MediaAsset) {
  return asset.kind === "video"
    ? <video src={mediaPreviewUrl(asset)} muted playsInline preload="metadata" />
    : <img src={mediaPreviewUrl(asset)} alt="" loading="lazy" />;
}

export function WysiwygEditor({ value, media, articleId, onChange, onMediaChange }: Props) {
  const [revision, setRevision] = useState(0);
  const [pickerMode, setPickerMode] = useState<PickerMode>(null);
  const [pickerStatus, setPickerStatus] = useState<MediaLibraryStatus>("ready");
  const [pickerError, setPickerError] = useState("");
  const [selectedMediaId, setSelectedMediaId] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [caption, setCaption] = useState("");
  const [posterMediaId, setPosterMediaId] = useState("");
  const [autoplay, setAutoplay] = useState(false);
  const [loop, setLoop] = useState(false);
  const [muted, setMuted] = useState(true);
  const [controls, setControls] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [duplicateAsset, setDuplicateAsset] = useState<MediaAsset | null>(null);
  const [nameWarning, setNameWarning] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const pickerRequest = useRef(0);
  const uploadInFlight = useRef(false);
  const lastEmitted = useRef(JSON.stringify(value));
  const mediaRef = useRef(media);
  mediaRef.current = media;

  const editor = useEditor({
    extensions: [StarterKit.configure({ blockquote: false, code: false, codeBlock: false, horizontalRule: false, strike: false, heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: false, linkOnPaste: false, HTMLAttributes: { rel: "noopener noreferrer" } } }), CitedBlockquote, ArticleImage, ArticleVideo, ArticleUnsupported, ArticleNote],
    content: articleDocumentToEditor(value, media),
    editorProps: { attributes: { class: "wysiwyg-canvas", "aria-label": "Содержимое статьи" } },
    onUpdate({ editor: current }) { const document = editorToArticleDocument(current.getJSON() as EditorNode); lastEmitted.current = JSON.stringify(document); onChange(document); setRevision((item) => item + 1); },
    onSelectionUpdate() { setRevision((item) => item + 1); },
  });

  useEffect(() => {
    if (!editor) return;
    const signature = JSON.stringify(value);
    if (signature !== lastEmitted.current) { editor.commands.setContent(articleDocumentToEditor(value, media), { emitUpdate: false }); lastEmitted.current = signature; }
  }, [editor, value, media]);

  const selectedAsset = useMemo(() => media.find((asset) => asset.id === selectedMediaId) ?? null, [media, selectedMediaId]);
  const pickerAssets = useMemo(() => media.filter((asset) => asset.kind === pickerMode), [media, pickerMode]);
  const imageAssets = useMemo(() => media.filter((asset) => asset.kind === "image"), [media]);

  async function openPicker(mode: Exclude<PickerMode, null>) {
    if (!editor) return;
    const nodeName = mode === "image" ? "articleImage" : "articleVideo";
    const editing = editor.isActive(nodeName);
    const attrs = editing ? editor.getAttributes(nodeName) : {};
    setSelectedMediaId(String(attrs.mediaId ?? media.find((asset) => asset.kind === mode)?.id ?? ""));
    setImageAlt(String(attrs.alt ?? ""));
    setCaption(String(attrs.caption ?? ""));
    setPosterMediaId(String(attrs.posterMediaId ?? ""));
    setAutoplay(Boolean(attrs.autoplay));
    setLoop(Boolean(attrs.loop));
    setMuted(attrs.muted !== false);
    setControls(attrs.controls !== false);
    setPickerError(""); setDuplicateAsset(null); setNameWarning("");
    setPickerMode(mode);
    const request = ++pickerRequest.current;
    setPickerStatus("loading");
    try {
      onMediaChange(mergeMediaAssets(mediaRef.current, await api.media(articleId)));
      if (request === pickerRequest.current) setPickerStatus("ready");
    } catch (reason) {
      if (request === pickerRequest.current) {
        setPickerError(reason instanceof Error ? reason.message : "Не удалось открыть медиатеку");
        setPickerStatus("error");
      }
    }
  }

  async function upload(file?: File) {
    if (!file || !canUpload(pickerStatus) || uploadInFlight.current) return;
    const request = pickerRequest.current;
    uploadInFlight.current = true;
    setUploading(true); setPickerStatus("uploading"); setPickerError(""); setDuplicateAsset(null); setNameWarning("");
    try {
      const known = await findDuplicate(file, mediaRef.current);
      if (known) { if (request === pickerRequest.current) setDuplicateAsset(known); return; }
      if (request === pickerRequest.current && mediaRef.current.some((asset) => hasSameNameAndMime(file, asset))) {
        setNameWarning("Файл с таким именем и типом уже есть, но его содержимое отличается. Существующий файл не будет перезаписан.");
      }
      const asset = await api.uploadMedia(file, articleId);
      onMediaChange(mergeMediaAssets(mediaRef.current, [asset]));
      if (request === pickerRequest.current && asset.kind === pickerMode) setSelectedMediaId(asset.id);
    } catch (reason) {
      const existing = duplicateMediaFromError(reason);
      if (existing) {
        if (request === pickerRequest.current) setDuplicateAsset(existing);
        onMediaChange(mergeMediaAssets(mediaRef.current, [existing]));
      } else if (request === pickerRequest.current) setPickerError(reason instanceof Error ? reason.message : "Не удалось загрузить медиафайл");
    } finally {
      uploadInFlight.current = false;
      if (request === pickerRequest.current) { setUploading(false); setPickerStatus("ready"); }
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function closePicker() { pickerRequest.current += 1; setPickerMode(null); }

  function retryPickerLoad() {
    if (!pickerMode) return;
    void openPicker(pickerMode);
  }

  function confirmMedia() {
    if (!editor || !selectedAsset || !pickerMode) return;
    if (pickerMode === "image") {
      if (!imageAlt.trim()) return;
      const attrs = { mediaId: selectedAsset.id, src: mediaPreviewUrl(selectedAsset), alt: imageAlt.trim(), caption: caption.trim() || null };
      if (editor.isActive("articleImage")) editor.chain().focus().updateAttributes("articleImage", attrs).run();
      else editor.chain().focus().insertContent({ type: "articleImage", attrs }).run();
    } else {
      const poster = imageAssets.find((asset) => asset.id === posterMediaId);
      const attrs = { mediaId: selectedAsset.id, src: mediaPreviewUrl(selectedAsset), posterMediaId: poster?.id ?? null, poster: poster ? mediaPreviewUrl(poster) : "", caption: caption.trim() || null, autoplay, loop, muted, controls };
      if (editor.isActive("articleVideo")) editor.chain().focus().updateAttributes("articleVideo", attrs).run();
      else editor.chain().focus().insertContent({ type: "articleVideo", attrs }).run();
    }
    closePicker();
  }

  function editLink() {
    if (!editor) return;
    const entered = window.prompt("Адрес ссылки: HTTPS, /внутренний-путь, #якорь или mailto:", String(editor.getAttributes("link").href ?? ""));
    if (entered === null) return;
    if (!entered.trim()) { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    const href = safeLink(entered);
    if (!href) { window.alert("Разрешены только HTTPS, внутренние ссылки, якоря и mailto:"); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  }

  if (!editor) return <div className="wysiwyg-loading">Открываем редактор…</div>;
  const blockType = editor.isActive("heading", { level: 2 }) ? "h2" : editor.isActive("heading", { level: 3 }) ? "h3" : "paragraph";
  const selectedNode = editor.isActive("articleImage") ? "image" : editor.isActive("articleVideo") ? "video" : null;
  return <div className="wysiwyg-shell" data-revision={revision}>
    <div className="wysiwyg-toolbar" role="toolbar" aria-label="Форматирование статьи">
      <select aria-label="Тип текста" value={blockType} onChange={(event) => { const chain = editor.chain().focus(); if (event.target.value === "h2") chain.toggleHeading({ level: 2 }).run(); else if (event.target.value === "h3") chain.toggleHeading({ level: 3 }).run(); else chain.setParagraph().run(); }}>
        <option value="paragraph">Обычный текст</option><option value="h2">Заголовок H2</option><option value="h3">Заголовок H3</option>
      </select>
      <span className="toolbar-separator" />
      <button type="button" className={editor.isActive("bold") ? "active" : ""} onMouseDown={(event) => { event.preventDefault(); editor.chain().focus().toggleBold().run(); }} title="Полужирный"><b>B</b></button>
      <button type="button" className={editor.isActive("italic") ? "active" : ""} onMouseDown={(event) => { event.preventDefault(); editor.chain().focus().toggleItalic().run(); }} title="Курсив"><i>I</i></button>
      <button type="button" className={editor.isActive("underline") ? "active" : ""} onMouseDown={(event) => { event.preventDefault(); editor.chain().focus().toggleUnderline().run(); }} title="Подчёркивание"><u>U</u></button>
      <span className="toolbar-separator" />
      <button type="button" className={editor.isActive("bulletList") ? "active" : ""} onMouseDown={(event) => { event.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}>• Список</button>
      <button type="button" className={editor.isActive("orderedList") ? "active" : ""} onMouseDown={(event) => { event.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}>1. Список</button>
      <button type="button" className={editor.isActive("blockquote") ? "active" : ""} onMouseDown={(event) => { event.preventDefault(); editor.chain().focus().toggleBlockquote().run(); }}>❝</button>
      <span className="toolbar-separator" />
      <button type="button" className={editor.isActive("link") ? "active" : ""} onMouseDown={(event) => { event.preventDefault(); editLink(); }}>↗ Ссылка</button>
      <button type="button" className={selectedNode === "image" ? "active" : ""} onMouseDown={(event) => { event.preventDefault(); void openPicker("image"); }}>▧ {selectedNode === "image" ? "Изменить" : "Изображение"}</button>
      <button type="button" className={selectedNode === "video" ? "active" : ""} onMouseDown={(event) => { event.preventDefault(); void openPicker("video"); }}>▶ {selectedNode === "video" ? "Изменить" : "Видео"}</button>
      <span className="toolbar-spacer" />
      <button type="button" onMouseDown={(event) => { event.preventDefault(); editor.chain().focus().undo().run(); }} disabled={!editor.can().undo()}>↶</button>
      <button type="button" onMouseDown={(event) => { event.preventDefault(); editor.chain().focus().redo().run(); }} disabled={!editor.can().redo()}>↷</button>
    </div>
    <EditorContent editor={editor} />
    <p className="wysiwyg-help">Enter — новый абзац · медиа вставляется в позицию курсора · настройки ролика доступны при выборе блока</p>
    {pickerMode ? <div className="media-picker-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closePicker(); }}>
      <section className="media-picker" role="dialog" aria-modal="true" aria-labelledby="media-picker-title">
        <header><div><h2 id="media-picker-title">{selectedNode ? "Изменить" : "Вставить"} {pickerMode === "image" ? "изображение" : "видео"}</h2><p>Выберите файл из медиатеки или загрузите новый.</p></div><button type="button" onClick={closePicker} aria-label="Закрыть">×</button></header>
        <input ref={fileRef} className="visually-hidden" disabled={!canUpload(pickerStatus)} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" onChange={(event) => void upload(event.target.files?.[0])} />
        <div className="media-picker-actions"><button type="button" className="secondary-button" disabled={!canUpload(pickerStatus)} onClick={() => fileRef.current?.click()}>{pickerStatus === "uploading" ? "Загрузка…" : "+ Загрузить медиа"}</button></div>
        {pickerError ? <div className="notice error" role="alert">{pickerError}</div> : null}
        {pickerStatus === "error" ? <div className="notice error" role="alert"><button type="button" className="quiet-button" onClick={retryPickerLoad}>Повторить загрузку медиатеки</button></div> : null}
        {nameWarning ? <div className="notice warning" role="status">{nameWarning}</div> : null}
        {duplicateAsset ? <div className="notice warning" role="status"><div><b>Такой файл уже есть в медиатеке</b><span>{duplicateAsset.originalName} · {duplicateAsset.mimeType} · {(duplicateAsset.byteSize / 1_048_576).toFixed(2)} МБ</span></div><button type="button" className="quiet-button" onClick={() => { setSelectedMediaId(duplicateAsset.id); if (pickerMode === "image" && !imageAlt) setImageAlt(duplicateAsset.originalName.replace(/\.[^.]+$/, "")); setDuplicateAsset(null); }}>Использовать существующий</button></div> : null}
        {pickerStatus === "loading" ? <p className="media-picker-empty">Загружаем медиатеку…</p> : pickerStatus !== "error" && pickerAssets.length ? <div className="media-picker-grid">{pickerAssets.map((asset) => <button type="button" className={selectedMediaId === asset.id ? "selected" : ""} key={asset.id} onClick={() => { setSelectedMediaId(asset.id); if (pickerMode === "image" && !imageAlt) setImageAlt(asset.originalName.replace(/\.[^.]+$/, "")); }}>{mediaPreview(asset)}<span>{asset.originalName}</span></button>)}</div> : pickerStatus !== "error" ? <p className="media-picker-empty">Подходящих файлов пока нет.</p> : null}
        {selectedAsset ? <div className="media-picker-fields">
          {pickerMode === "image" ? <label>Alt изображения<input value={imageAlt} onChange={(event) => setImageAlt(event.target.value)} maxLength={300} required /></label> : <label>Постер (необязательно)<select value={posterMediaId} onChange={(event) => setPosterMediaId(event.target.value)}><option value="">Без постера</option>{imageAssets.map((asset) => <option key={asset.id} value={asset.id}>{asset.originalName}</option>)}</select></label>}
          <label>Подпись <input value={caption} onChange={(event) => setCaption(event.target.value)} maxLength={300} placeholder="Необязательно" /></label>
          {pickerMode === "video" ? <div className="video-settings"><label><input type="checkbox" checked={autoplay} onChange={(event) => setAutoplay(event.target.checked)} />Автовоспроизведение</label><label><input type="checkbox" checked={loop} onChange={(event) => setLoop(event.target.checked)} />Повтор</label><label><input type="checkbox" checked={muted} onChange={(event) => setMuted(event.target.checked)} />Без звука</label><label><input type="checkbox" checked={controls} onChange={(event) => setControls(event.target.checked)} />Панель управления</label></div> : null}
        </div> : null}
        <footer>{selectedNode ? <button type="button" className="danger-link" onClick={() => { editor.chain().focus().deleteSelection().run(); closePicker(); }}>Удалить из статьи</button> : <span />}<div><button type="button" className="quiet-button" onClick={closePicker}>Отмена</button><button type="button" className="primary-button" disabled={!selectedAsset || pickerMode === "image" && !imageAlt.trim()} onClick={confirmMedia}>{selectedNode ? "Сохранить" : "Вставить"}</button></div></footer>
      </section>
    </div> : null}
  </div>;
}
